interface CachedPermissions {
  data: Array<unknown>;
  timestamp: number;
  version: string;
}

/**
 * The subset of `LocalStorageService` these helpers use. Typed structurally (rather than importing the class
 * from `@ups/xplat/core`) so this file adds no `security -> xplat-core` edge to the module graph.
 */
export interface PermissionCacheStorage {
  getItem<T>(key: string): T | undefined;
  setItem<T>(key: string, value: T): void;
  removeItem(key: string): void;
}

/** The subset of `LogService` these helpers use. Structural for the same reason as {@link PermissionCacheStorage}. */
export interface PermissionCacheLogger {
  warn(...args: unknown[]): void;
}

const CACHE_VERSION = '1.0';

/** The localStorage key holding the cached permissions for an `identityKey()`. */
export const permissionCacheKey = (identityKey: string): string =>
  `ups_security_${identityKey}`;
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * PERFORMANCE OPTIMIZATION: cache security permissions in localStorage so subsequent page loads are
 * near-instant (no API call needed). Extracted verbatim from SecurityService as part of the #34116 file-size
 * split; behavior is unchanged.
 */
export function savePermissionsToCache(
  storage: PermissionCacheStorage,
  log: PermissionCacheLogger,
  key: string,
  data: Array<unknown>
): void {
  try {
    const cacheData: CachedPermissions = {
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };
    storage.setItem(key, JSON.stringify(cacheData));
  } catch (error) {
    log.warn('SecurityService: Failed to cache permissions', error);
  }
}

/** Returns cached permissions when present and younger than 24h; otherwise clears the entry and returns null. */
export function loadPermissionsFromCache(
  storage: PermissionCacheStorage,
  log: PermissionCacheLogger,
  key: string
): Array<unknown> | null {
  try {
    const cached = storage.getItem<string>(key);
    if (!cached) return null;

    const cacheData = JSON.parse(cached) as CachedPermissions;
    const cacheAge = Date.now() - cacheData.timestamp;

    // Return cached data if it's still fresh.
    if (cacheAge < CACHE_MAX_AGE_MS && cacheData.version === CACHE_VERSION) {
      return cacheData.data;
    }

    // Cache expired, remove it.
    storage.removeItem(key);
    return null;
  } catch (error) {
    log.warn('SecurityService: Failed to load cached permissions', error);
    return null;
  }
}
